import { Loader2, Mic, RotateCcw, Square } from "lucide-react";

import { Button } from "@src/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/shared/components/ui/tooltip";
import { cn } from "@src/shared/lib/utils";

import { useVoiceInput } from "../../application";
import {
  DEFAULT_LABELS,
  STATUS_MESSAGES,
  type UseVoiceInputResult,
  type VoiceInputButtonLabels,
  type VoiceInputConfig,
  type VoiceInputState,
} from "../../domain";

interface VoiceInputButtonBaseProps {
  config: VoiceInputConfig;
  onTranscript: (text: string, transcriptionId: string) => void;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  iconClassName?: string;
  labels?: VoiceInputButtonLabels;
  /**
   * Drops the visible label only while idle (icon-only "Speak" affordance)
   * — used inline in the agenda composer pill (STITCH-551) where there
   * isn't room for text next to the mic icon. Transcribing/processing/error
   * always keep their label regardless of this prop: those are the states
   * where the label is the only feedback the user gets ("Creating
   * items...", "Try again"), so hiding it there would remove real
   * information, not just redundant text. Recording is also always
   * icon-only regardless of this prop (see `VoiceInputButtonView`'s
   * `isCompactIcon`) — it matches the reference prototype's always-icon
   * "Stop" control, and its label surfaces via a tooltip instead. The
   * accessible name is unaffected either way: the button's `aria-label`
   * already carries it independent of visible content, so hiding the label
   * never removes the name a screen reader announces or an e2e test
   * queries by role.
   */
  hideLabel?: boolean;
}

export type VoiceInputButtonProps = VoiceInputButtonBaseProps;

export interface VoiceInputButtonViewProps extends UseVoiceInputResult {
  className?: string;
  buttonClassName?: string;
  iconClassName?: string;
  hideLabel?: boolean;
  disabled: boolean;
  isIdle: boolean;
  isRecording: boolean;
  isTranscribing: boolean;
  isError: boolean;
  buttonLabel: string;
  statusMessage: string;
  primaryAction: () => void;
}

export interface BuildVoiceInputButtonViewPropsOptions {
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  iconClassName?: string;
  labels?: VoiceInputButtonLabels;
  hideLabel?: boolean;
}

/**
 * Pure derivation of `VoiceInputButtonViewProps` from a `useVoiceInput()` result — no hooks of its
 * own. Extracted (STITCH-551) so the agenda composer, which needs the raw `voiceInput` object
 * itself (to swap its text input for a real audio meter while recording, driven by
 * `voiceInput.getMediaStream`), can build the *same* view props `VoiceInputButton` uses below
 * without duplicating this wiring or standing up a second, disconnected `useVoiceInput` instance.
 */
export function buildVoiceInputButtonViewProps(
  voiceInput: UseVoiceInputResult,
  options: BuildVoiceInputButtonViewPropsOptions = {},
): VoiceInputButtonViewProps {
  const {
    disabled = false,
    className,
    buttonClassName,
    iconClassName,
    labels,
    hideLabel,
  } = options;
  const isIdle = voiceInput.state === "idle";
  const isRecording = voiceInput.state === "recording";
  const isTranscribing = voiceInput.state === "transcribing";
  const isProcessing = voiceInput.state === "processing";
  const isError = voiceInput.state === "error";
  const resolvedLabels = { ...DEFAULT_LABELS, ...labels };
  const buttonLabel = voiceInput.isSupported
    ? resolvedLabels[voiceInput.state]
    : resolvedLabels.unsupported;

  return {
    ...voiceInput,
    className,
    buttonClassName,
    iconClassName,
    hideLabel,
    disabled:
      disabled || !voiceInput.isSupported || isTranscribing || isProcessing,
    isIdle,
    isRecording,
    isTranscribing,
    isError,
    buttonLabel,
    statusMessage: STATUS_MESSAGES[voiceInput.state],
    primaryAction: () => {
      if (isRecording) {
        voiceInput.stopRecording();
        return;
      }

      if (isError) {
        voiceInput.dismissError();
      }

      void voiceInput.startRecording();
    },
  };
}

export function VoiceInputButton(props: VoiceInputButtonProps) {
  const {
    config,
    onTranscript,
    disabled,
    className,
    buttonClassName,
    iconClassName,
    labels,
    hideLabel,
  } = props;
  const voiceInput = useVoiceInput({ config, onTranscript });
  const viewProps = buildVoiceInputButtonViewProps(voiceInput, {
    disabled,
    className,
    buttonClassName,
    iconClassName,
    labels,
    hideLabel,
  });

  return <VoiceInputButtonView {...viewProps} />;
}

export function VoiceInputButtonView({
  className,
  buttonClassName,
  iconClassName,
  ...renderProps
}: VoiceInputButtonViewProps) {
  // `hideLabel` only suppresses the label while idle (see the prop's doc
  // comment) — transcribing/processing/error still render one, so the
  // button can't be pinned to a fixed icon-only square in every state
  // without clipping that text. Recording is a second, unconditional
  // icon-only case (STITCH-551, matches the reference prototype's always-
  // icon "Stop" control): unlike idle, it doesn't depend on `hideLabel` —
  // recording never had a label short enough to earn one, and the
  // composer replaces the whole input row with a live audio meter at the
  // same time, so there's no room to gain by keeping text here either.
  // Idle and recording keep the compact square; transcribing/processing/
  // error grow to fit their label, capped by a max-width + truncation so a
  // long label (e.g. "processing") can't consume the whole composer pill.
  // `buttonClassName` is applied last and wins ties via tailwind-merge, so
  // callers can still override shape (radius, shrink), but should not
  // reintroduce a fixed width/height here — that reopens the clipping bug
  // this guards against.
  const isCompactIcon =
    (renderProps.hideLabel && renderProps.isIdle) || renderProps.isRecording;

  return (
    <div className={cn("inline-flex items-center", className)}>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={renderProps.buttonLabel}
              className={cn(
                "h-8 gap-1.5 rounded-[8px] text-[11px] font-medium text-muted-foreground shadow-none hover:bg-primary/5 hover:text-primary disabled:opacity-45",
                isCompactIcon
                  ? "w-8 shrink-0 justify-center p-0"
                  : "w-auto max-w-32 shrink-0 justify-start px-2 py-1",
                renderProps.isRecording &&
                  "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:text-destructive-foreground",
                (renderProps.isTranscribing ||
                  renderProps.state === "processing") &&
                  "text-primary hover:text-primary",
                renderProps.isError &&
                  "text-destructive hover:text-destructive",
                buttonClassName,
              )}
              disabled={renderProps.disabled}
              onClick={renderProps.primaryAction}
              type="button"
              variant="ghost"
            >
              <VoiceInputButtonIcon
                className={cn("shrink-0", iconClassName)}
                state={renderProps.state}
              />
              {isCompactIcon ? null : (
                <span className="min-w-0 truncate">
                  {renderProps.buttonLabel}
                </span>
              )}
            </Button>
          </TooltipTrigger>
          {/*
           * The tooltip only ever renders content for the icon-only states
           * (idle-hideLabel "Speak", recording "Stop") — every other state
           * already shows its label as text, so a duplicate tooltip would
           * just double-expose the same string to sighted mouse users.
           * The wrapper itself (`TooltipProvider` > `Tooltip` >
           * `TooltipTrigger`) stays unconditional across *every* state so
           * the underlying `<Button>` never gains or loses an ancestor
           * between renders — that keeps it the same DOM node end to end
           * (idle → recording → transcribing → ...), which is what lets
           * focus survive every transition below without any manual
           * restoration.
           */}
          {isCompactIcon ? (
            <TooltipContent side="top">
              {renderProps.buttonLabel}
            </TooltipContent>
          ) : null}
        </Tooltip>
      </TooltipProvider>
      <span className="sr-only" role="status" aria-live="polite">
        {renderProps.error ?? renderProps.statusMessage}
      </span>
    </div>
  );
}

function VoiceInputButtonIcon({
  className,
  state,
}: {
  className?: string;
  state: VoiceInputState;
}) {
  switch (state) {
    case "recording":
      return <Square aria-hidden="true" className={cn("size-5", className)} />;
    case "transcribing":
    case "processing":
      return (
        <Loader2
          aria-hidden="true"
          className={cn("size-5 animate-spin", className)}
        />
      );
    case "error":
      return (
        <RotateCcw aria-hidden="true" className={cn("size-5", className)} />
      );
    case "idle":
      return <Mic aria-hidden="true" className={cn("size-5", className)} />;
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}
